import { formatDistanceToNow } from 'date-fns';
import { History } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import type { AuditLog } from '@/types/api';

import { formatBytes as fmtBytes } from '@/lib/formatBytes';

interface Props {
  entries: AuditLog[];
  isLoading?: boolean;
  isError?: boolean;
  error?: unknown;
  selectedId?: string | null;
  onSelect?: (entry: AuditLog | null) => void;
}

function actionLabel(entry: AuditLog): string {
  const { action, metadata } = entry;
  const outcome =
    typeof metadata?.outcome === 'string' ? (metadata.outcome as string) : null;
  switch (action) {
    case 'file.upload':
      return 'uploaded the file';
    case 'file.version.upload':
    case 'file.version.create':
      return 'uploaded a new version';
    case 'file.download':
      return 'downloaded the file';
    case 'file.version.download':
      return 'downloaded a version';
    case 'file.rename':
      return 'renamed the file';
    case 'file.move':
      return 'moved the file';
    case 'file.delete':
      return 'deleted the file';
    case 'file.restore':
      return 'restored the file';
    case 'file_request.create':
    case 'request.create':
      return 'created this request';
    case 'file_request.submit':
    case 'request.submit':
      return 'submitted a file';
    case 'file_request.review':
    case 'request.review':
      return outcome ? `reviewed (${outcome})` : 'reviewed the request';
    case 'request.review.accepted':
      return 'accepted the submission';
    case 'request.review.rejected':
      return 'rejected the submission';
    case 'file_request.cancel':
    case 'request.cancel':
      return 'cancelled the request';
    default:
      return action.replace(/[._]/g, ' ');
  }
}

function renderMetadata(entry: AuditLog): string | null {
  const meta = entry.metadata;
  if (!meta) return null;

  const parts: string[] = [];

  if (entry.action === 'file.rename') {
    const from = meta.from;
    const to = meta.to;
    if (typeof from === 'string' && typeof to === 'string') {
      parts.push(`"${from}" → "${to}"`);
    }
  } else if (
    entry.action === 'file.upload' ||
    entry.action === 'file.version.upload' ||
    entry.action === 'file.version.create'
  ) {
    if (typeof meta.version === 'number') parts.push(`v${meta.version}`);
    if (typeof meta.name === 'string') parts.push(meta.name);
    if (typeof meta.size === 'number') parts.push(fmtBytes(meta.size));
  } else if (entry.action === 'file.version.download') {
    if (typeof meta.version === 'number') parts.push(`v${meta.version}`);
  } else if (entry.action === 'file.move') {
    if (typeof meta.from_folder === 'string' && typeof meta.to_folder === 'string') {
      parts.push('to a different folder');
    }
  } else if (
    entry.action === 'request.review.accepted' ||
    entry.action === 'request.review.rejected' ||
    entry.action === 'file_request.review' ||
    entry.action === 'request.review'
  ) {
    if (typeof meta.note === 'string' && meta.note.trim()) {
      parts.push(`note: ${meta.note}`);
    }
  } else if (entry.action === 'request.create' || entry.action === 'file_request.create') {
    if (typeof meta.title === 'string') parts.push(meta.title);
  }

  return parts.length ? parts.join(' · ') : null;
}

export function ActivityTimeline({ entries, isLoading, isError, error, selectedId, onSelect }: Props) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner />
      </div>
    );
  }

  if (isError) {
    const msg =
      error instanceof Error ? error.message : 'Failed to load activity.';
    return <div className="text-sm text-red-600">{msg}</div>;
  }

  if (!entries || entries.length === 0) {
    return (
      <EmptyState
        icon={<History size={24} />}
        title="No activity yet"
        description="Actions taken on this item will appear here."
      />
    );
  }

  return (
    <ol className="relative border-l border-slate-200 ml-3 pl-5 space-y-4">
      {entries.map((entry) => {
        const extra = renderMetadata(entry);
        const when = formatDistanceToNow(new Date(entry.created_at), {
          addSuffix: true,
        });
        const isSelected = selectedId === entry.id;
        return (
          <li
            key={entry.id}
            className={`relative rounded-md pr-2 -mr-2 transition-colors ${onSelect ? 'cursor-pointer hover:bg-[#F2F2F8]' : ''} ${isSelected ? 'bg-[#EFF0FF]' : ''}`}
            onClick={() => onSelect?.(isSelected ? null : entry)}
          >
            <span
              className={`absolute -left-[27px] top-1.5 h-2.5 w-2.5 ${isSelected ? 'bg-[#0410BD]' : 'bg-bb-navy'}`}
              aria-hidden="true"
            />
            <div className="text-sm text-slate-800 pt-1">
              <span className="font-semibold">
                {entry.actor_name ?? 'Someone'}
              </span>{' '}
              <span className="text-slate-600">{actionLabel(entry)}</span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5 pb-1">{when}</div>
            {extra && (
              <div className="text-xs text-slate-500 mb-1">{extra}</div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
